import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useValidateInviteQuery, useAcceptInviteMutation } from '../store/authApi';
import AuthLayout from './AuthLayout';

const AcceptInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const { data: invite, isLoading, error } = useValidateInviteQuery(token, { skip: !token });
  const [acceptInvite, { isLoading: accepting }] = useAcceptInviteMutation();

  const [form, setForm] = useState({ name: '', designation: '', password: '', confirm: '' });

  useEffect(() => {
    if (invite) {
      setForm(prev => ({
        ...prev,
        name: prev.name || invite.name || '',
        designation: prev.designation || invite.designation || '',
      }));
    }
  }, [invite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Full name is required');
    if (!form.designation.trim()) return toast.error('Designation is required');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.password !== form.confirm) return toast.error('Passwords do not match');

    try {
      await acceptInvite({
        token,
        name: form.name,
        designation: form.designation,
        password: form.password,
      }).unwrap();
      toast.success('Account created! Please sign in.');
      navigate('/login');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to accept invitation');
    }
  };

  if (!token) {
    return (
      <AuthLayout isLogin>
        <div className="card bg-red-50 border-red-200 text-center">
          <h2 className="font-display font-bold text-xl text-red-700 mb-2">Invalid Invitation Link</h2>
          <p className="text-sm text-red-600 mb-4">No invitation token provided.</p>
          <Link to="/login" className="btn-primary inline-block">Back to Sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  if (isLoading) {
    return (
      <AuthLayout isLogin>
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  if (error || !invite) {
    const message = (error as any)?.data?.message || 'This invitation link is invalid or has expired.';
    return (
      <AuthLayout isLogin>
        <div className="card bg-red-50 border-red-200 text-center">
          <h2 className="font-display font-bold text-xl text-red-700 mb-2">Invitation Not Available</h2>
          <p className="text-sm text-red-600 mb-4">{message}</p>
          <Link to="/login" className="btn-primary inline-block">Go to Sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  const labelCls = "block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5";

  return (
    <AuthLayout isLogin={false}>
      <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-trident-orange-light border border-trident-orange/30">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        <span className="text-[10px] font-display font-bold uppercase tracking-wider text-trident-orange">
          Invitation Verified
        </span>
      </div>

      <h2 className="font-display font-extrabold text-3xl text-trident-gray-900 mb-2">Welcome aboard!</h2>
      <p className="text-trident-gray-500 mb-6">
        You've been invited as a <strong className="text-trident-orange">{invite.role}</strong>. Complete your profile to activate your account.
      </p>

      <div className="mb-5 p-3 rounded-xl bg-trident-gray-50 border border-trident-gray-100">
        <div className="text-xs text-trident-gray-500 mb-0.5">Account email</div>
        <div className="font-display font-semibold text-trident-gray-900 text-sm">{invite.email}</div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Full Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input-field"
            placeholder="Jane Doe"
            required
          />
        </div>
        <div>
          <label className={labelCls}>Designation</label>
          <input
            type="text"
            value={form.designation}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
            className="input-field"
            placeholder="e.g., QA Engineer"
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input-field"
              placeholder="Min 6 chars"
              required
              minLength={6}
            />
          </div>
          <div>
            <label className={labelCls}>Confirm</label>
            <input
              type="password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="input-field"
              placeholder="Confirm password"
              required
              minLength={6}
            />
          </div>
        </div>
        <button type="submit" disabled={accepting} className="btn-primary w-full justify-center flex items-center gap-2 py-3 mt-2">
          {accepting ? 'Creating account...' : 'Create my account'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default AcceptInvite;
